import {useQuery} from '@tanstack/react-query'
import {Clock} from 'lucide-react'
import {api} from '@/api/client'
import {endpoints} from '@/api/endpoints'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {CardSection} from '@/components/shared/CardSection'
import {HistoryEntry} from '@/components/shared/HistoryEntry'
import {InlineEmptyText} from '@/components/shared/InlineEmptyText'
import {InlineRow} from '@/components/shared/InlineRow'
import {Stack} from '@/components/shared/Stack'
import {Strong} from '@/components/shared/Strong'
import {MutedText} from '@/components/shared/MutedText'
import {formatTimestamp} from '@/lib/date-utils'
import {useI18n} from '@/i18n'
import {
    EscalationEvent,
    EscalationEventType,
    EscalationLevel,
    EscalationStepType,
} from '@/types/escalation'

interface EscalationTimelineCardProps {
    stepId: string
    stepType: EscalationStepType
    escalationLevel?: EscalationLevel | null
    nextDeadlineAt?: string | null
    platformOwnerName?: string | null
    'data-testid'?: string
}

function levelVariant(level: EscalationLevel | null | undefined): BadgeVariant {
    if (level == null) {
        return 'outline'
    }
    switch (level) {
        case EscalationLevel.REMINDED:
            return 'secondary'
        case EscalationLevel.FINAL_REMINDED:
        case EscalationLevel.ESCALATED_TO_MANAGER:
            return 'warning'
        case EscalationLevel.ESCALATED_TO_PLATFORM_OWNER:
            return 'destructive'
        case EscalationLevel.CHAIN_CLOSED:
            return 'success'
        case EscalationLevel.NONE:
        default:
            return 'outline'
    }
}

function eventVariant(eventType: EscalationEventType): BadgeVariant {
    switch (eventType) {
        case EscalationEventType.REMINDED:
            return 'secondary'
        case EscalationEventType.FINAL_REMINDED:
        case EscalationEventType.ESCALATED_TO_MANAGER:
            return 'warning'
        case EscalationEventType.ESCALATED_TO_PLATFORM_OWNER:
            return 'destructive'
        case EscalationEventType.CHAIN_CLOSED:
            return 'success'
        default:
            return 'outline'
    }
}

export function EscalationTimelineCard({
    stepId,
    stepType,
    escalationLevel,
    nextDeadlineAt,
    platformOwnerName,
    'data-testid': testId = 'escalation-timeline-card',
}: EscalationTimelineCardProps) {
    const i18n = useI18n()

    const {data: events = []} = useQuery({
        queryKey: ['escalation-events', stepId, stepType],
        queryFn: () => api.get<EscalationEvent[]>(endpoints.escalationEvents.list(stepId, stepType)),
        enabled: !!stepId,
    })

    const levels = i18n.escalation.timeline.levels
    const eventTypes = i18n.escalation.timeline.eventTypes
    const currentLevel = escalationLevel ?? EscalationLevel.NONE
    const isTerminal = currentLevel === EscalationLevel.CHAIN_CLOSED
    const showNextDeadline = nextDeadlineAt && !isTerminal

    return (
        <CardSection
            icon={Clock}
            title={i18n.escalation.timeline.title}
            data-testid={testId}
            data-help-prefix="escalationTimeline"
        >
            <Stack gap="md">
                <Stack gap="sm">
                    <InlineRow gap="sm">
                        <MutedText size="sm">{i18n.escalation.timeline.currentLevel}</MutedText>
                        <Badge
                            data-testid={`${testId}-level-badge`}
                            variant={levelVariant(currentLevel)}
                        >
                            {levels[currentLevel as keyof typeof levels]}
                        </Badge>
                    </InlineRow>
                    {showNextDeadline && (
                        <InlineRow gap="sm" data-testid={`${testId}-next-deadline`}>
                            <MutedText size="sm">{i18n.escalation.timeline.nextDeadline}</MutedText>
                            <Strong size="sm">{formatTimestamp(nextDeadlineAt)}</Strong>
                        </InlineRow>
                    )}
                    {platformOwnerName && (
                        <InlineRow gap="sm" data-testid={`${testId}-platform-owner`}>
                            <MutedText size="sm">{i18n.escalation.timeline.platformOwner}</MutedText>
                            <Strong size="sm">{platformOwnerName}</Strong>
                        </InlineRow>
                    )}
                </Stack>

                {events.length === 0 ? (
                    <InlineEmptyText data-testid={`${testId}-empty`}>
                        {i18n.escalation.timeline.empty}
                    </InlineEmptyText>
                ) : (
                    <Stack gap="sm">
                        {events.map((event) => (
                            <HistoryEntry key={event.id} data-testid={`${testId}-event-${event.id}`}>
                                <InlineRow gap="md" wrap>
                                    <Badge variant={eventVariant(event.eventType)}>
                                        {eventTypes[event.eventType as keyof typeof eventTypes]}
                                    </Badge>
                                    <MutedText size="sm">{formatTimestamp(event.firedAt)}</MutedText>
                                    {event.recipientName && (
                                        <Strong size="sm" data-testid={`${testId}-event-${event.id}-recipient`}>
                                            {event.recipientName}
                                        </Strong>
                                    )}
                                    {event.note && (
                                        <MutedText size="sm">
                                            {i18n.escalation.timeline.unresolvableManager}
                                        </MutedText>
                                    )}
                                </InlineRow>
                            </HistoryEntry>
                        ))}
                    </Stack>
                )}
            </Stack>
        </CardSection>
    )
}
